// Currency handling.
//
// The engine itself is currency-agnostic: every monetary number it reads
// (costPerRole.annualCost, spendPerFte.ratio, budgetBuckets.minUsd/maxUsd,
// profile.totalBudget) and every monetary number it produces is expressed in
// the model's *base* currency. Nothing here is applied during calculation —
// these helpers exist so the display layer and portfolio reporting can convert
// finished numbers.
//
// USD is the hub for all conversions. Each non-USD currency the model knows
// about carries a single `perUsd` rate, so converting between any two known
// currencies is `perUsd(to) / perUsd(from)`. A model whose base is not USD must
// therefore quote its own currency against USD; that one rate is what makes
// both USD display and USD portfolio rollups possible.

import type { CurrencyCode, CurrencyConfig, Model } from './types.js';

export const USD: CurrencyCode = 'USD';

export interface CurrencyMeta {
  code: CurrencyCode;
  symbol: string;
  label: string;
}

/** Common currencies offered in the model editor's base/display pickers. */
export const CURRENCIES: CurrencyMeta[] = [
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
  { code: 'CAD', symbol: 'CA$', label: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar' },
  { code: 'NZD', symbol: 'NZ$', label: 'New Zealand Dollar' },
  { code: 'JPY', symbol: '¥', label: 'Japanese Yen' },
  { code: 'CNY', symbol: 'CN¥', label: 'Chinese Yuan' },
  { code: 'HKD', symbol: 'HK$', label: 'Hong Kong Dollar' },
  { code: 'SGD', symbol: 'S$', label: 'Singapore Dollar' },
  { code: 'INR', symbol: '₹', label: 'Indian Rupee' },
  { code: 'CHF', symbol: 'CHF', label: 'Swiss Franc' },
  { code: 'SEK', symbol: 'kr', label: 'Swedish Krona' },
  { code: 'PLN', symbol: 'zł', label: 'Polish Zloty' },
  { code: 'MXN', symbol: 'MX$', label: 'Mexican Peso' },
  { code: 'BRL', symbol: 'R$', label: 'Brazilian Real' },
  { code: 'ZAR', symbol: 'R', label: 'South African Rand' },
  { code: 'AED', symbol: 'AED', label: 'UAE Dirham' },
];

const CURRENCY_BY_CODE = new Map(CURRENCIES.map((c) => [c.code, c]));

export function currencyMeta(code: CurrencyCode | undefined): CurrencyMeta | undefined {
  return code ? CURRENCY_BY_CODE.get(code) : undefined;
}

/** Display symbol for a code, falling back to the code itself. */
export function currencySymbol(code: CurrencyCode | undefined): string {
  return currencyMeta(code)?.symbol || code || '$';
}

export function currencyLabel(code: CurrencyCode | undefined): string {
  const meta = currencyMeta(code);
  return meta ? `${meta.code} — ${meta.label}` : code || '';
}

/** A rate is only usable if it is a finite positive number. */
function isUsableRate(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Normalized currency config for a model. Models saved before currency support
 * (and models that never left USD) have no `currency` field, so they read as
 * plain USD with no alternate display options.
 */
export function modelCurrency(model: Pick<Model, 'currency'> | undefined | null): CurrencyConfig {
  const cfg = model?.currency;
  const base = cfg?.base || USD;
  const rates = Array.isArray(cfg?.rates) ? cfg!.rates : [];
  return { base, rates, displayDefault: cfg?.displayDefault };
}

/** Units of `code` per 1 USD, or null when the model has no usable rate for it. */
export function perUsd(
  cfg: CurrencyConfig,
  code: CurrencyCode | undefined,
): number | null {
  if (!code) return null;
  if (code === USD) return 1;
  const row = cfg.rates.find((r) => r.code === code);
  return row && isUsableRate(row.perUsd) ? row.perUsd : null;
}

/**
 * Currencies the model's outputs may be displayed in: its base currency first,
 * then USD, then any other currency with a usable rate. Anything without a
 * rate is excluded, which is what keeps the output picker limited to what the
 * model actually set up.
 */
export function displayCurrencyOptions(
  model: Pick<Model, 'currency'> | undefined | null,
): CurrencyCode[] {
  const cfg = modelCurrency(model);

  // The base currency needs no conversion, so it is always offered.
  const out: CurrencyCode[] = [cfg.base];

  // Reaching any other currency means going through USD, which is impossible
  // when the base itself is not quoted against USD.
  const fromBase = perUsd(cfg, cfg.base);
  if (fromBase === null) return out;

  const push = (code: CurrencyCode | undefined) => {
    if (!code || out.includes(code)) return;
    if (perUsd(cfg, code) === null) return;
    out.push(code);
  };

  push(USD);
  for (const rate of cfg.rates) push(rate.code);
  return out;
}

/**
 * Multiplier converting an amount from the model's base currency into `to`.
 * Returns 1 when the conversion cannot be resolved, so a misconfigured model
 * shows unconverted numbers rather than NaN.
 */
export function conversionFactor(
  model: Pick<Model, 'currency'> | undefined | null,
  to: CurrencyCode | undefined,
): number {
  const cfg = modelCurrency(model);
  if (!to || to === cfg.base) return 1;
  const from = perUsd(cfg, cfg.base);
  const target = perUsd(cfg, to);
  if (from === null || target === null) return 1;
  return target / from;
}

/**
 * Multiplier converting the model's monetary values into USD, or null when the
 * model has a non-USD base and never quoted it against USD. Portfolio
 * reporting uses null to skip a scenario rather than mix currencies into a USD
 * total.
 */
export function toUsdFactor(model: Pick<Model, 'currency'> | undefined | null): number | null {
  const cfg = modelCurrency(model);
  if (cfg.base === USD) return 1;
  const from = perUsd(cfg, cfg.base);
  if (from === null) return null;
  return 1 / from;
}

/**
 * Validation messages for a model's currency setup. Empty means the config is
 * safe to save.
 */
export function currencyConfigIssues(
  model: Pick<Model, 'currency'> | undefined | null,
): string[] {
  const cfg = modelCurrency(model);
  const issues: string[] = [];

  if (cfg.base !== USD && perUsd(cfg, cfg.base) === null) {
    issues.push(
      `This model's values are in ${cfg.base}, so it needs a ${cfg.base}-per-USD rate. Without it, outputs cannot be shown in USD and the model is excluded from portfolio reporting.`,
    );
  }

  const seen = new Set<CurrencyCode>();
  for (const rate of cfg.rates) {
    if (!rate.code) {
      issues.push('A conversion rate is missing its currency.');
      continue;
    }
    if (rate.code === USD) {
      issues.push('USD is the reference currency and does not need its own rate.');
      continue;
    }
    if (seen.has(rate.code)) {
      issues.push(`${rate.code} has more than one rate.`);
    }
    seen.add(rate.code);
    if (!isUsableRate(rate.perUsd)) {
      issues.push(`${rate.code} needs a rate greater than zero.`);
    }
  }

  const fallback = displayCurrencyOptions(model);
  if (cfg.displayDefault && !fallback.includes(cfg.displayDefault)) {
    issues.push(
      `Default output currency ${cfg.displayDefault} is not one of this model's display options.`,
    );
  }

  return issues;
}

/** Currency outputs should open in: the model's default, else its base. */
export function defaultDisplayCurrency(
  model: Pick<Model, 'currency'> | undefined | null,
): CurrencyCode {
  const cfg = modelCurrency(model);
  const options = displayCurrencyOptions(model);
  if (cfg.displayDefault && options.includes(cfg.displayDefault)) return cfg.displayDefault;
  return options[0] || cfg.base;
}
